import { TwoUIProgressViewOperationData } from "@amx/ux-twoui-telemetry";
import { Theme, useTheme } from "@amx/ux-twoui-theming";
import { IStackStyles, IStyle, SpinnerSize, Stack, Text as FluentText } from "@fluentui/react";
import React, { useState } from "react";
import { useComponentLogger } from "../../shared/logging";
import { InteractiveElementProps } from "../../shared/props";
import { isCharacterALetter, isNumber } from "../../shared/utilities";
import { IconName } from "../../styles/icons";
import { IconButton } from "../Buttons/IconButton/IconButton";
import { Icon } from "../Icon/Icon";
import { ShimmerProgressView } from "../Shimmer/ShimmerProgressView/ShimmerProgressView";
import { Spinner } from "../Spinner/Spinner";
import { StopPropagation } from "../StopPropagation/StopPropagation";
import { SensitiveContentShimmer } from "./SensitiveContentShimmer/SensitiveContentShimmer";

export const asteriskIconName: IconName = "CircleFillBadge12";
export const hideSensitiveContentIconName: IconName = "Hide";
export const showSensitiveContentIconName: IconName = "RevealPasswordMedium";

export type SensitiveContentType = "password";

const maximumAsteriskNumber = 25;
const textFieldMaxWidth = 120;
const iconButtonWidth = 48;
export interface SensitiveContentIconButtonProps extends InteractiveElementProps {
    /**
     * Text to show when user hover/focus the Icon button
     */
    readonly tooltip: string;
}

export interface SensitiveContentOperationalData {
    /**
     * Information about the monitored operation.
     */
    readonly operationData: TwoUIProgressViewOperationData;
    /**
     * aria-label for spinner waiting for decryption to complete
     */
    readonly spinnerAriaLabel: string;
}

export interface SensitiveContentProps {
    /**
     * What type the sensitive content is
     */
    readonly contentType: SensitiveContentType;
    /**
     * A callback function which is called after the hide icon button has been clicked
     */
    readonly onHideButtonClick?: () => void;
    /**
     * A callback function which is called after the show icon button has been clicked
     */
    readonly onShowButtonClick?: () => void;
    /**
     * Operational data for SensitiveContent component
     */
    readonly operationalData?: SensitiveContentOperationalData;
    /**
     * Props for show icon Button
     */
    readonly showIconButton: SensitiveContentIconButtonProps;
    /**
     * Props for hide icon Button
     */
    readonly hideIconButton: SensitiveContentIconButtonProps;
    /**
     * Clear text for sensitive content
     */
    readonly value?: string;
}

interface ContentProps {
    /**
     * Determine sensitive content is visible or not
     */
    readonly shown: boolean;
    /**
     * Any kind of info
     */
    readonly value?: string;
    /**
     * What sensitive content it is
     */
    readonly contentType: SensitiveContentType;
}

interface ShownSensitiveContentProps {
    /**
     * Sensitive info
     */
    readonly value: string;
    /**
     * What sensitive content it is
     */
    readonly contentType: SensitiveContentType;
}

interface IconButtonInternalProps extends SensitiveContentIconButtonProps {
    /**
     * Icon name for the icon button
     */
    readonly iconName: IconName;
}

const highlightSpecialCharacters = (color: string, char: string, index: number) => {
    return (
        <FluentText key={`${char}-${index}`} style={{ color: color }}>{char}</FluentText>
    );
};

const getStyledContent = (value: string, theme: Theme, type: SensitiveContentType) => {
    const styledContent: JSX.Element[] = [];
    switch (type) {
        case "password": {
            Array.from(value).forEach((char, index) => {
                if (isNumber(char)) {
                    styledContent.push(highlightSpecialCharacters(theme.semanticColors.errorBlockIcon, char, index));
                } else if (isCharacterALetter(char)) {
                    styledContent.push(highlightSpecialCharacters(theme.palette.neutralPrimary, char, index));
                } else {
                    styledContent.push(highlightSpecialCharacters(theme.semanticColors.messageLink, char, index));
                }
            });
            break;
        }
    }
    return styledContent;
};

const ShownSensitiveContent: React.FC<ShownSensitiveContentProps> = ({
    contentType,
    value
}) => {
    const theme = useTheme();
    const styledContent = getStyledContent(value, theme, contentType);
    const shownPasswordContainerStyles: IStackStyles = {
        root: {
            display: "flex",
            flexWrap: "wrap"
        }
    };

    return (
        <Stack horizontal={true} styles={shownPasswordContainerStyles}>
            {styledContent}
        </Stack>
    );
};

const HiddenSensitiveContent: React.FC = () => {
    // tslint:disable-next-line: prefer-array-literal
    const hiddenContent = [...Array(maximumAsteriskNumber)].map((_, i) => (
        <Icon
            key={i}
            iconName={asteriskIconName}
            size="6px"
            color="neutralSecondary"
        />
    ));

    const hiddenPasswordContainerStyles: IStackStyles = {
        root: {
            display: "flex",
            flexWrap: "wrap",
            overflow: "hidden",
            height: "6px",
        }
    };

    return (
        <Stack horizontal={true} styles={hiddenPasswordContainerStyles} >
            {hiddenContent}
        </Stack>
    );
};

const Content: React.FC<ContentProps> = ({
    contentType,
    shown,
    value,
}) => {
    if (shown && value) {
        return <ShownSensitiveContent value={value} contentType={contentType} />;
    } else {
        return <HiddenSensitiveContent />;
    }
};

export const SensitiveContent: React.FC<SensitiveContentProps> = ({
    contentType,
    onHideButtonClick,
    onShowButtonClick,
    operationalData,
    showIconButton,
    hideIconButton,
    value
}) => {
    useComponentLogger("SensitiveContent");

    const [hideSensitiveContent, setHideSensitiveContent] = useState<boolean>(true);
    const [revealControlButton, setRevealControlButton] = useState<IconButtonInternalProps>({
        iconName: showSensitiveContentIconName,
        tooltip: showIconButton.tooltip,
        "aria-label": showIconButton["aria-label"],
        "data-bi-id": showIconButton["data-bi-id"],
        disabled: showIconButton.disabled,
    });

    const iconButtonClick = () => {
        if (hideSensitiveContent) {
            setHideSensitiveContent(false);
            updateRevealControlButton(true);
            if (onShowButtonClick) {
                onShowButtonClick();
            }
        } else {
            setHideSensitiveContent(true);
            updateRevealControlButton(false);
            if (onHideButtonClick) {
                onHideButtonClick();
            }
        }
    };

    const updateRevealControlButton = (hide: boolean) => {
        setRevealControlButton({
            iconName: hide ? hideSensitiveContentIconName : showSensitiveContentIconName,
            tooltip: hide ? hideIconButton.tooltip : showIconButton.tooltip,
            "aria-label": hide ? hideIconButton["aria-label"] : showIconButton["aria-label"],
            "data-bi-id": hide ? hideIconButton["data-bi-id"] : showIconButton["data-bi-id"],
            disabled: hide ? hideIconButton.disabled : showIconButton.disabled,
        });
    };

    const containerStyles: IStyle = {
        display: "flex",
        justifyContent: "space-between",
        maxWidth: `calc(${textFieldMaxWidth}px + ${iconButtonWidth}px)`
    };

    const updatingView = (
        <Stack
            horizontal={true}
            verticalAlign="center"
            tokens={{ childrenGap: "8px" }}
            styles={{
                root: containerStyles
            }}
        >
            <Content
                shown={false}
                value={value}
                contentType={contentType}
            />
            <Stack styles={{ root: { height: "32px", padding: "0px 16px 0px 8px" } }} verticalAlign="center">
                <Spinner
                    aria-label={operationalData?.spinnerAriaLabel!}
                    spinnerSize={SpinnerSize.small}
                />
            </Stack>
        </Stack>
    );

    const baseComponent = (
        <Stack
            horizontal={true}
            verticalAlign="center"
            tokens={{ childrenGap: "8px" }}
            styles={{
                root: containerStyles
            }}
        >
            <Content
                shown={!hideSensitiveContent}
                value={value}
                contentType={contentType}
            />
            <Stack styles={{ root: { height: "32px", marginRight: "8px" } }} verticalAlign="center">
                <StopPropagation>
                    <IconButton
                        iconName={revealControlButton.iconName}
                        iconColor="neutralSecondary"
                        disabled={revealControlButton.disabled}
                        onClick={iconButtonClick}
                        aria-label={revealControlButton["aria-label"]}
                        data-bi-id={revealControlButton["data-bi-id"]}
                        tooltipHostProps={{
                            tooltipText: revealControlButton.tooltip
                        }}
                    />
                </StopPropagation>
            </Stack>
        </Stack>
    );

    const failedView = (
        <Stack
            horizontal={true}
            verticalAlign="center"
            tokens={{ childrenGap: "8px" }}
            styles={{
                root: containerStyles
            }}
        >
            <Content
                shown={false}
                value={undefined}
                contentType={contentType}
            />
            <Stack styles={{ root: { height: "32px", marginRight: "8px" } }} verticalAlign="center">
                <StopPropagation>
                    <IconButton
                        iconName={showSensitiveContentIconName}
                        iconColor="neutralSecondary"
                        disabled={showIconButton.disabled}
                        onClick={iconButtonClick}
                        aria-label={showIconButton["aria-label"]}
                        data-bi-id={showIconButton["data-bi-id"]}
                        tooltipHostProps={{
                            tooltipText: showIconButton.tooltip
                        }}
                    />
                </StopPropagation>
            </Stack>
        </Stack>
    );

    if (operationalData) {
        return (
            <ShimmerProgressView
                operationData={operationalData.operationData}
                inertView={baseComponent}
                firstLoadView={<SensitiveContentShimmer />}
                failedView={failedView}
                updatingView={updatingView}
                updateFailedView={baseComponent}
                finishedView={baseComponent}
            />
        );
    } else {
        return baseComponent;
    }
};
